/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ExternalLink, Clock, User } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { cn } from '../../../../lib/utils';

function ArticleCard({ article }) {
  const [imageError, setImageError] = useState(false);
  const hasImage = article.image && !imageError;

  const formatDate = (date) => {
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <a href={article.link} target="_blank" rel="noopener noreferrer" className="group block">
      <div
        className={cn(
          'flex flex-col h-full rounded-xl bg-card overflow-hidden transition-all',
          'hover:shadow-md hover:-translate-y-1',
        )}
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        {/* Image - only show if available */}
        {hasImage && (
          <div className="relative h-40 bg-muted overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
              onError={() => setImageError(true)}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col flex-1 p-4">
          {/* Source Badge */}
          <div className="flex items-center justify-between mb-2">
            <Badge variant="secondary" className="text-xs gap-1">
              <span>{article.sourceIcon}</span>
              {article.source}
            </Badge>
            <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          {/* Title */}
          <h3 className="font-semibold text-base mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {article.title}
          </h3>

          {/* Description */}
          {article.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3 flex-1">
              {article.description}
            </p>
          )}

          {/* Meta */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto pt-3 border-t">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDate(article.pubDate)}
            </div>
            {article.author && (
              <div className="flex items-center gap-1 truncate">
                <User className="h-3 w-3 shrink-0" />
                <span className="truncate">{article.author}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </a>
  );
}

ArticleCard.propTypes = {
  article: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    description: PropTypes.string,
    pubDate: PropTypes.instanceOf(Date).isRequired,
    author: PropTypes.string,
    image: PropTypes.string,
    source: PropTypes.string.isRequired,
    sourceIcon: PropTypes.string.isRequired,
    sourceColor: PropTypes.string,
  }).isRequired,
};

export default ArticleCard;
